import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { usePostsStore } from '../../stores/postsStore';
import Post from '../Post';
import CreatePost from '../CreatePost';
import Friends from '../Friends';

const PostWrapper = ({ post, viewingProfileId, isHighlighted, openReplies }) => {
  const postRef = useRef(null);

  useEffect(() => {
    if (isHighlighted && postRef.current) {
      setTimeout(() => {
        postRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [isHighlighted]);

  return (
    <div
      ref={postRef}
      className={isHighlighted ? 'ring-4 ring-primary-400 rounded-lg' : ''}
    >
      <Post post={post} viewingProfileId={viewingProfileId} openReplies={openReplies} />
    </div>
  );
};

/**
 * Dashboard Widget - Posts feed with create post functionality
 * Can be embedded in CMS pages
 * @param {number} profileId - The profile ID whose posts to display
 */
const DashboardWidget = ({ profileId }) => {
  const [searchParams] = useSearchParams();
  const { accessToken, profile } = useAuthStore();
  const { posts, isLoading, hasMore, fetchPosts, jumpToPost, selectedDate, selectedPostId, selectedPostHasReply, resetToLatest } = usePostsStore();
  const observerTarget = useRef(null);
  const hasFetched = useRef(false);
  const [canPost, setCanPost] = useState(false);

  // Handle back to top
  const handleBackToTop = async () => {
    if (!accessToken || !profileId) return;
    await resetToLatest(accessToken, profileId);
  };

  // Check if user can post on this profile
  useEffect(() => {
    if (!accessToken || !profileId) return;

    const checkPostPermission = async () => {
      try {
        const response = await fetch(`/api/profile/can-post/${profileId}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        const data = await response.json();
        if (data.success) {
          setCanPost(data.can_post);
        }
      } catch (error) {
        console.error('Failed to check post permission:', error);
        setCanPost(false);
      }
    };

    checkPostPermission();
  }, [accessToken, profileId]);

  // Check URL params for jumpToPost (from notifications)
  useEffect(() => {
    const jumpToPostParam = searchParams.get('jumpToPost');
    const replyParam = searchParams.get('reply');

    if (jumpToPostParam && accessToken && profileId && !hasFetched.current) {
      hasFetched.current = true;
      const postId = parseInt(jumpToPostParam);
      const isReply = !!replyParam;
      jumpToPost(accessToken, postId, profileId, isReply);
    }
  }, [searchParams, accessToken, profileId, jumpToPost]);

  // Fetch initial posts only once (skip if jumpToPost param exists)
  useEffect(() => {
    const jumpToPostParam = searchParams.get('jumpToPost');

    if (accessToken && !hasFetched.current && !jumpToPostParam) {
      hasFetched.current = true;
      fetchPosts(accessToken, true, profileId);
    }
  }, [accessToken, fetchPosts, profileId, searchParams]);

  // Infinite scroll observer
  useEffect(() => {
    if (!accessToken) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && hasFetched.current) {
          fetchPosts(accessToken, false, profileId);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, isLoading, accessToken, fetchPosts, profileId]);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Column - Posts Feed (3/4 width) */}
      <div className="flex-1 lg:w-3/4">
        <div className="space-y-6">
          {/* Create Post - only show if user can post on this profile */}
          {canPost && <CreatePost postOnProfileId={profileId} />}

          {/* Back to Top Button - Show when date or post is selected */}
          {(selectedDate || selectedPostId) && (
            <div className="flex justify-center">
              <button
                onClick={handleBackToTop}
                className="w-10 h-10 rounded-full bg-gray-500 hover:bg-gray-600 text-white flex items-center justify-center transition-colors shadow-lg"
                title="Back to Latest"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* Posts Feed */}
          <div className="space-y-6">
            {posts.length === 0 && !isLoading && hasFetched.current && (
              <div className="surface rounded-lg shadow-lg p-8 text-center">
                <p className="text-secondary">No posts yet. Be the first to share something!</p>
              </div>
            )}

            {posts.map((post) => (
              <PostWrapper
                key={post.id}
                post={post}
                viewingProfileId={profileId}
                isHighlighted={post.id === selectedPostId}
                openReplies={post.id === selectedPostId && selectedPostHasReply}
              />
            ))}

            {/* Loading Indicator - only show on subsequent loads */}
            {isLoading && hasFetched.current && posts.length > 0 && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
              </div>
            )}

            {/* Infinite Scroll Target - only render when we have posts */}
            {posts.length > 0 && <div ref={observerTarget} className="h-4" />}
          </div>
        </div>
      </div>

      {/* Right Column - Friends Sidebar (1/4 width) */}
      <div className="lg:w-1/4">
        <div className="sticky top-6">
          <Friends profileIdOverride={profileId} />
        </div>
      </div>
    </div>
  );
};

export default DashboardWidget;
